import { PRIVILEGES } from 'constants/privileges';
import { useState, useRef, useLayoutEffect } from 'react';
import {
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { MappingParams } from 'api/externalServiceCode/externalServiceCode.types';
import { CustomTablePagination, StyledTableHeadCell } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { ShippingService, ServiceCode } from '../../mapping.types';
import {
  collapse,
  counterpartiesPlural,
  mainColumnHeaders,
  notSpecified,
  servicesPlural,
} from '../MappingModule.consts';
import * as S from './MappingTable.css';

const MAX_VISIBLE_CP = 3;
const noBorder = { borderBottom: 'none' } as const;

function renderWithBlanks(name: string): React.ReactNode {
  if (!name.includes('|')) return name;
  return name.split(/\|+/).reduce<React.ReactNode[]>((acc, part, i) => {
    if (i > 0)
      acc.push(
        <S.BlankRect
          key={`b${i}`}
          style={acc.length === 0 ? { marginLeft: 0 } : undefined}
        />,
      );
    if (part) acc.push(part);
    return acc;
  }, []);
}

function polishPlural(n: number, one: string, few: string, many: string) {
  if (n === 1) return one;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function CodesCell({
  codes,
  entryId,
  expanded,
  onToggle,
}: {
  codes: ServiceCode[];
  entryId: string;
  expanded: boolean;
  onToggle: (id: string) => void;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(codes.length);

  useLayoutEffect(() => {
    if (expanded) {
      setVisibleCount(codes.length);
      return;
    }

    const inner = innerRef.current;
    if (!inner) return;

    const measure = () => {
      const chips = Array.from(
        inner.querySelectorAll<HTMLElement>('[data-chip]'),
      );
      if (!chips.length) {
        setVisibleCount(0);
        return;
      }

      const innerRight = inner.getBoundingClientRect().right;
      let count = 0;
      for (const chip of chips) {
        if (chip.getBoundingClientRect().right <= innerRight + 1) count++;
        else break;
      }
      setVisibleCount(Math.max(1, count));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [codes, expanded]);

  const shown = codes.slice(0, visibleCount);
  const hiddenCount = codes.length - visibleCount;

  return (
    <S.CodesContainer $expanded={expanded}>
      {expanded ? (
        <>
          {codes.map((code, index) => (
            <S.CodeChip key={code.id}>
              {renderWithBlanks(code.code)}
              {index < codes.length - 1 ? ',' : ''}
            </S.CodeChip>
          ))}
          <S.ExpandLink
            onClick={(event) => {
              event.stopPropagation();
              onToggle(entryId);
            }}
          >
            {collapse}
          </S.ExpandLink>
        </>
      ) : (
        <>
          <S.CodesInner ref={innerRef}>
            {shown.map((code, index) => (
              <S.CodeChip key={code.id} data-chip=''>
                {renderWithBlanks(code.code)}
                {index < shown.length - 1 ? ',' : ''}
              </S.CodeChip>
            ))}
          </S.CodesInner>
          <S.ExpandLink
            style={
              hiddenCount === 0
                ? { visibility: 'hidden', pointerEvents: 'none' }
                : undefined
            }
            onClick={(event) => {
              event.stopPropagation();
              onToggle(entryId);
            }}
          >
            +{hiddenCount}{' '}
            {polishPlural(
              hiddenCount,
              servicesPlural.plural1,
              servicesPlural.plural2,
              servicesPlural.plural3,
            )}
          </S.ExpandLink>
        </>
      )}
    </S.CodesContainer>
  );
}

export function MappingTable({
  data,
  count,
  params,
  setParams,
}: {
  data: ShippingService[];
  count: number;
  params: MappingParams;
  setParams: React.Dispatch<React.SetStateAction<MappingParams>>;
}) {
  const navigate = useNavigate();
  const { isAuthorized } = useAuth();
  const canViewDetails = isAuthorized(
    PRIVILEGES.Settings.VIEW_DISCRIMINANT_DETAIL,
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [hoveredServiceId, setHoveredServiceId] = useState<string | null>(null);
  const [expandedCp, setExpandedCp] = useState<Record<string, boolean>>({});
  const [expandedCodes, setExpandedCodes] = useState<Record<string, boolean>>(
    {},
  );

  const toggleCp = (id: string) =>
    setExpandedCp((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleCodes = (id: string) =>
    setExpandedCodes((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked ? paginatedData.map((service) => service.id) : [],
    );

  const handleSelect = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const paginatedData = data.slice(
    (params.pageNumber - 1) * params.resultSize,
    params.pageNumber * params.resultSize,
  );

  const renderServiceRows = (service: ShippingService) => {
    const sortedCounterparties = [...service.counterparties].sort(
      (a, b) => b.codes.length - a.codes.length,
    );
    const isExpanded = expandedCp[service.id] ?? false;
    const visible = isExpanded
      ? sortedCounterparties
      : sortedCounterparties.slice(0, MAX_VISIBLE_CP);
    const hiddenCp = sortedCounterparties.length - MAX_VISIBLE_CP;
    const isSelected = selected.includes(service.id);
    const totalRows = visible.length + (hiddenCp > 0 ? 1 : 0);
    const isHovered = hoveredServiceId === service.id;
    const hoverBg = isHovered ? 'rgba(242, 242, 242, 1)' : undefined;
    const goToDetails = canViewDetails
      ? () => navigate(`/mapping/${service.id}`)
      : undefined;

    return (
      <TableBody
        key={service.id}
        onMouseEnter={() => setHoveredServiceId(service.id)}
        onMouseLeave={() => setHoveredServiceId(null)}
      >
        {visible.map((counterparty, index) => (
          <TableRow
            key={counterparty.id}
            onClick={goToDetails}
            sx={{
              '& > td': noBorder,
              cursor: canViewDetails ? 'pointer' : 'default',
              backgroundColor: hoverBg,
            }}
          >
            {index === 0 && (
              <>
                <TableCell
                  padding='checkbox'
                  rowSpan={totalRows}
                  sx={{ verticalAlign: 'top', backgroundColor: hoverBg }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    color='primary'
                    checked={isSelected}
                    onClick={(event) => handleSelect(event, service.id)}
                  />
                </TableCell>
                <TableCell
                  rowSpan={totalRows}
                  sx={{
                    verticalAlign: 'top',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    backgroundColor: hoverBg,
                    ...noBorder,
                  }}
                >
                  {service.serviceName}
                </TableCell>
              </>
            )}
            <TableCell
              sx={{
                verticalAlign: 'top',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...noBorder,
              }}
            >
              {counterparty.name ?? (
                <span style={{ opacity: 0.6 }}>{notSpecified}</span>
              )}
            </TableCell>
            <TableCell sx={noBorder}>
              <CodesCell
                codes={counterparty.codes}
                entryId={counterparty.id}
                expanded={expandedCodes[counterparty.id] ?? false}
                onToggle={toggleCodes}
              />
            </TableCell>
          </TableRow>
        ))}
        {hiddenCp > 0 && (
          <TableRow
            onClick={goToDetails}
            sx={{
              '& > td': noBorder,
              cursor: canViewDetails ? 'pointer' : 'default',
              backgroundColor: hoverBg,
            }}
          >
            <TableCell colSpan={2} sx={noBorder}>
              <S.ExpandLink
                onClick={(event) => {
                  event.stopPropagation();
                  toggleCp(service.id);
                }}
              >
                {isExpanded
                  ? collapse
                  : `+${hiddenCp} ${polishPlural(
                      hiddenCp,
                      counterpartiesPlural.plural1,
                      counterpartiesPlural.plural2,
                      counterpartiesPlural.plural3,
                    )}`}
              </S.ExpandLink>
            </TableCell>
          </TableRow>
        )}
        <TableRow sx={{ backgroundColor: hoverBg }}>
          <TableCell
            colSpan={4}
            sx={{ p: 0, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
          />
        </TableRow>
      </TableBody>
    );
  };

  return (
    <>
      <CustomTablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        count={count}
        rowsPerPage={params.resultSize}
        page={params.pageNumber - 1}
        onPageChange={(_, newPage) =>
          setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }))
        }
        onRowsPerPageChange={(event) => {
          setParams({
            resultSize: parseInt(event.target.value, 10),
            pageNumber: 1,
          });
        }}
      />
      <Divider />
      <TableContainer>
        <Table
          sx={{ minWidth: 750, tableLayout: 'fixed', width: '100%' }}
          size='medium'
        >
          <TableHead>
            <TableRow>
              <StyledTableHeadCell padding='checkbox' sx={{ width: 48 }}>
                <Checkbox
                  color='primary'
                  indeterminate={
                    selected.length > 0 &&
                    selected.length < paginatedData.length
                  }
                  checked={
                    paginatedData.length > 0 &&
                    selected.length === paginatedData.length
                  }
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'Select all services' }}
                />
              </StyledTableHeadCell>
              <StyledTableHeadCell sx={{ width: '22%' }}>
                {mainColumnHeaders.internalActivity}
              </StyledTableHeadCell>
              <StyledTableHeadCell sx={{ width: '18%' }}>
                {mainColumnHeaders.counterparty}
              </StyledTableHeadCell>
              <StyledTableHeadCell>
                {mainColumnHeaders.externalActivity}
              </StyledTableHeadCell>
            </TableRow>
          </TableHead>
          {paginatedData.map(renderServiceRows)}
        </Table>
      </TableContainer>
      <CustomTablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        count={count}
        rowsPerPage={params.resultSize}
        page={params.pageNumber - 1}
        onPageChange={(_, newPage) =>
          setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }))
        }
        onRowsPerPageChange={(event) => {
          setParams({
            resultSize: parseInt(event.target.value, 10),
            pageNumber: 1,
          });
        }}
      />
    </>
  );
}
